/**
 * TreatmentGroupsTableV16 - v16 Handsontable table component
 * Uses all v16 editors, validators, and renderers
 */
import type { FC, RefObject } from 'react';
import { useMemo } from 'react';
import SpreadsheetV16 from '@/components/UI/SpreadSheet/SpreadsheetV16';
import type { SpreadsheetV16Settings } from '@/components/UI/SpreadSheet/SpreadsheetV16.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';
import { ColorEditorV16 } from '@/components/UI/SpreadSheet/editors-v16/ColorEditorV16';
import type { MetadataField } from '@/model/Metadata.model';
import { _isNil, _notNil } from '@/littledash';
import type { TreatmentGroupKeyedMetadata } from './CreateTreatmentGroups';
import { settingsV16 as baseSettingsV16, metadataToColumnSettingMapperV16 } from './CreateTreatmentGroups.utils';
import { type CustomColumnSettingsV16 } from '../UI/SpreadSheet/SpreadsheetV16.model';

interface TreatmentGroupsTableV16Props {
  groupData: { groups: Array<TreatmentGroupKeyedMetadata> };
  setGroupData: (data: { groups: Array<TreatmentGroupKeyedMetadata> }) => void;
  columnData: {
    colHeaders: Array<string>;
    columns: Array<CustomColumnSettingsV16>;
  };
  updateColumnData: (data: { colHeaders: Array<string>; columns: Array<CustomColumnSettingsV16> }) => void;
  metadata: Array<MetadataField>;
  setMetadata: (metadata: Array<MetadataField>) => void;
  setIsInvalid: (invalid: boolean) => void;
  innerRef: RefObject<HotTableRef | null>;
}

const TreatmentGroupsTableV16: FC<TreatmentGroupsTableV16Props> = ({
  groupData,
  setGroupData,
  columnData,
  updateColumnData,
  metadata,
  setMetadata,
  setIsInvalid,
  innerRef,
}) => {
  const settings = useMemo<SpreadsheetV16Settings>(
    () => ({
      ...baseSettingsV16,
      colHeaders: columnData.colHeaders,
      columns: columnData.columns,
      contextMenu: {
        items: {
          row_above: {},
          row_below: {},
          remove_row: {},
        },
      },
      manualColumnResize: true,
    }),
    [columnData]
  );

  const patchMetadataAfterSettingsUpdated = () => {
    // Sync mutated settings back to parent state
    updateColumnData({
      colHeaders: settings.colHeaders as Array<string>,
      columns: settings.columns as Array<CustomColumnSettingsV16>,
    });
  };

  const handleAfterCreateRow = () => {
    requestAnimationFrame(() => {
      const updatedGroups = [...groupData.groups];
      for (let rowIndex = 0; rowIndex < updatedGroups.length - 1; rowIndex++) {
        const group = updatedGroups[rowIndex];
        if (_isNil(group.color)) {
          group.color = ColorEditorV16.colorForIndex(rowIndex);
        }
        group.no = rowIndex;
      }
      setGroupData({ groups: updatedGroups });
    });
  };

  const handleAfterRemoveRow = () => {
    const hot = innerRef.current?.hotInstance;
    if (_notNil(hot)) {
      hot.validateCells();
    }
  };

  return (
    <SpreadsheetV16
      className="studyGroups"
      testPrefix="treatment-groups-v16"
      data={groupData.groups as unknown as Array<Record<string, unknown>>}
      innerRef={innerRef as RefObject<HotTableRef>}
      settings={settings as SpreadsheetV16Settings}
      autoAddRow
      metadata={metadata}
      metadataToColumnSettingMapper={metadataToColumnSettingMapperV16}
      afterCreateRow={handleAfterCreateRow}
      setMetadata={setMetadata}
      setInvalid={setIsInvalid}
      afterRemoveRow={handleAfterRemoveRow}
      patchMetadataAfterSettingsUpdated={patchMetadataAfterSettingsUpdated}
    />
  );
};

export default TreatmentGroupsTableV16;
